'use client';

import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { withBasePath } from '@/lib/base-path';
import type { Branding } from '@/types';

/**
 * O formulario nao fala com a API diretamente: envia para a rota do proprio
 * painel, que guarda os tokens em cookies httpOnly.
 */
export function LoginForm({ branding }: { branding: Branding }) {
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [showTenantField, setShowTenantField] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(withBasePath('/api/session/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          ...(tenantSlug ? { tenantSlug } : {}),
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = String(payload?.message ?? 'Não foi possível entrar.');
        // A API pede a empresa quando o mesmo e-mail existe em mais de um cliente.
        if (message.toLowerCase().includes('mais de uma empresa')) {
          setShowTenantField(true);
        }
        setError(message);
        return;
      }

      router.replace('/');
      router.refresh();
    } catch {
      setError('Falha de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="mb-8 lg:hidden">
        {branding.assets.logoPrimary ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={branding.assets.logoPrimary}
            alt={branding.platformName}
            className="h-14 w-auto object-contain"
          />
        ) : (
          <p className="notranslate text-2xl font-bold tracking-tight text-ink" translate="no">
            {branding.platformName}
          </p>
        )}
      </div>

      <h1 className="text-2xl font-semibold tracking-tight text-ink">
        {branding.login.title ?? 'Bem-vindo'}
      </h1>
      <p className="mt-1.5 text-sm text-ink-muted">
        {branding.login.subtitle ?? 'Entre com suas credenciais para continuar.'}
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="voce@empresa.com.br"
          leading={<Mail className="h-4 w-4" />}
          disabled={loading}
        />

        <Input
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="••••••••"
          leading={<Lock className="h-4 w-4" />}
          disabled={loading}
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="rounded p-0.5 transition hover:text-ink"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
        />

        {showTenantField ? (
          <Input
            label="Empresa"
            value={tenantSlug}
            onChange={(event) => setTenantSlug(event.target.value)}
            placeholder="identificador-da-empresa"
            hint="Seu e-mail está vinculado a mais de uma empresa."
            disabled={loading}
          />
        ) : null}

        {error ? (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}

        <Button type="submit" size="lg" loading={loading} className="w-full">
          Entrar
        </Button>
      </form>

      <footer className="mt-10 space-y-2 text-center text-xs text-ink-subtle">
        {branding.supportEmail ? (
          <p>
            Precisa de ajuda?{' '}
            <a
              href={`mailto:${branding.supportEmail}`}
              className="font-medium text-brand hover:underline"
            >
              {branding.supportEmail}
            </a>
          </p>
        ) : null}
        <p className="notranslate" translate="no">
          {branding.footerText ?? branding.companyName ?? branding.platformName}
        </p>
      </footer>
    </div>
  );
}
